// @ts-nocheck

import React, { useCallback, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import { getCurrency, getDecimalSeparator } from '../../i18n/l10n';
import theme from '../../styled-components/dark';

const WrapStake = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid;
  border-radius: 1.2em;
  border-color: ${({ $focused, $hasError }) =>
    $focused
      ? theme.StakeInput.focusedBorderColor
      : $hasError
        ? theme.StakeInput.errorColor
        : theme.StakeInput.borderColor};

  transition: border-color 150ms ease-in-out;
  width: 100%;
  margin: 0.4em 0;
  padding: 0 0.8em;
`;

const Placeholder = styled.span`
  color: ${({ $hasError }) => ($hasError ? theme.StakeInput.errorColor : theme.StakeInput.placeholderColor)};
  display: flex;
  align-items: center;
  font-size: 90%;
  width: 100%;
`;

const commonInput = css`
  border: none;
  border-radius: 0;
  padding: 0 0.3125em;
  text-align: right;
  width: 100%;
  box-sizing: border-box;
  background-color: transparent;
  color: ${theme.StakeInput.color};
  font-size: 1.35em;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  &:focus {
    outline: none;
  }
  :disabled {
    opacity: 0.5;
  }
`;

const Input = styled.input`
  ${commonInput}
`;

const FakeInput = styled.div`
  ${commonInput}
  min-height: 1.3em;
`;

const Currency = styled.div`
  color: ${theme.StakeInput.color};
  pointer-events: none;
  font-size: 1.2em;
  ${({ $shift }) => ($shift !== null ? `transform: translateX(-${$shift * 0.52 + 1}em);` : '')}
`;

type StakeInputProps = {
  className?: string;
  stake: string;
  placeholderLabel: string;
  onStakeChange: (stake: string) => void;
  hasError?: boolean;
  leftSymbol?: boolean;
  disabled?: boolean;
  onFocus?: () => void;
};

const StakeInput = ({
  placeholderLabel,
  stake,
  onStakeChange,
  hasError = false,
  leftSymbol,
  className,
  disabled,
  onFocus,
}: StakeInputProps) => {
  const ref = useRef(null);
  const [focus, setFocus] = useState(false);
  // we replace the stake raw value by the international one
  const formattedStake = stake ? stake.toString().replace(/(,|\.)+/, getDecimalSeparator()) : '';

  const handleFocus = useCallback(() => {
    setFocus(true);
    if (onFocus) {
      onFocus();
    }
    if (ref.current) {
      ref.current.focus();
    }
  }, [setFocus, onFocus]);

  const handleStakeChangeFromKeyboard = useCallback(
    (e) => {
      if (!disabled && typeof e === 'object' && 'target' in e) {
        onStakeChange(e.target.value.replace(/(,|\.)+/, '.'));
      }
    },
    [onStakeChange, disabled],
  );

  const selectOnClick = useCallback((e) => {
    e.target.select();
  }, []);

  return (
    <WrapStake className={className} $focused={focus} $hasError={hasError} onClick={handleFocus}>
      {placeholderLabel ? <Placeholder $hasError={hasError}>{placeholderLabel}</Placeholder> : null}
      {Target.__MOBILE__ ? (
        <FakeInput ref={ref} disabled={disabled} tabIndex={0} onBlur={() => setFocus(false)}>
          {formattedStake}
        </FakeInput>
      ) : (
        <Input
          ref={ref}
          type="text"
          onChange={handleStakeChangeFromKeyboard}
          onClick={selectOnClick}
          disabled={disabled}
          value={formattedStake}
          onBlur={() => setFocus(false)}
        />
      )}
      <Currency shift={leftSymbol ? stake?.length || 0 : null}>{getCurrency()}</Currency>
    </WrapStake>
  );
};

export default StakeInput;
